import React, { useState } from "react";
import { createQuote } from "../api";
import 'bootstrap/dist/css/bootstrap.min.css';

const RequestQuoteForm = ({ onClose, currentLanguage }) => {
  const [formData, setFormData] = useState({
    full_name: "",
    phone: "",
    email: "",
    best_time_to_contact: "",
    number_of_passengers: "",
    destination_from: "",
    destination_to: "",
  });

  const translations = {
    EN: {
      title: "Request a Quote",
      fullName: "Full Name",
      phone: "Phone",
      email: "Email",
      bestTimeToContact: "Best Time to Contact",
      numberOfPassengers: "Number of Passengers",
      destinationFrom: "Pickup Location",
      destinationTo: "Drop-off Location",
      submit: "Submit",
      close: "Close",
    },
    AR: {
      title: "طلب عرض",
      fullName: "الاسم الكامل",
      phone: "الهاتف",
      email: "البريد الإلكتروني",
      bestTimeToContact: "أفضل وقت للاتصال",
      numberOfPassengers: "عدد الركاب",
      destinationFrom: "مكان الالتقاط",
      destinationTo: "وجهة الوصول",
      submit: "إرسال",
      close: "إغلاق",
    },
    ES: {
      title: "Solicitar Cotización",
      fullName: "Nombre Completo",
      phone: "Teléfono",
      email: "Correo Electrónico",
      bestTimeToContact: "Mejor Momento para Contactar",
      numberOfPassengers: "Número de Pasajeros",
      destinationFrom: "Ubicación de Recogida",
      destinationTo: "Ubicación de Entrega",
      submit: "Enviar",
      close: "Cerrar",
    },
  };

  const [success, setSuccess] = useState(false);
  const bestTimeOptions = [
    { value: "morning", label: { EN: "Morning", AR: "الصباح", ES: "Mañana" } },
    { value: "afternoon", label: { EN: "Afternoon", AR: "بعد الظهر", ES: "Tarde" } },
    { value: "evening", label: { EN: "Evening", AR: "المساء", ES: "Noche" } },
    { value: "weekend", label: { EN: "Weekend", AR: "عطلة نهاية الأسبوع", ES: "Fin de semana" } },
  ];

  const labels = translations[currentLanguage];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createQuote(formData);
      setSuccess(true);
      // Reset form
      setFormData({
        full_name: "",
        phone: "",
        email: "",
        best_time_to_contact: "",
        number_of_passengers: "",
        destination_from: "",
        destination_to: "",
      });
      // Close modal after 3 seconds to show success message
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 3000);
    } catch (error) {
      console.error("Error requesting quote:", error);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1050
    }}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{
        backgroundColor: 'white',
        borderRadius: '2rem',
        padding: '2rem',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 8px 32px 0 rgba(31,38,135,0.12)',
        position: 'relative'
      }}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">💰 {labels.title}</h2>
            <p className="text-muted mb-0">Get a personalized quote in minutes</p>
          </div>
          <button 
            type="button" 
            className="btn-close" 
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            ✕
          </button>
        </div>
        
        {success && (
          <div className="alert alert-success" role="alert">
            <div className="text-center">
              <div className="mb-2">✅</div>
              <strong>Quote Request Submitted Successfully!</strong>
              <div className="mt-1">We'll get back to you with a customized quote within 24 hours</div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="row mb-3">
            <div className="col-md-6 mb-3">
              <input
                type="text"
                className="form-control"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                placeholder={labels.fullName}
                required
              />
            </div>
            <div className="col-md-6 mb-3">
              <input
                type="tel"
                className="form-control"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder={labels.phone}
                required
              />
            </div>
          </div>
          
          <div className="mb-3">
            <input
              type="email"
              className="form-control"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={labels.email}
              required
            />
          </div>
          
          <div className="row mb-3">
            <div className="col-md-6 mb-3">
              <select
                className="form-select"
                name="best_time_to_contact"
                value={formData.best_time_to_contact}
                onChange={handleChange}
                required
              >
                <option value="">{labels.bestTimeToContact}</option>
                {bestTimeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label[currentLanguage]}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6 mb-3">
              <input
                type="number"
                className="form-control"
                name="number_of_passengers"
                value={formData.number_of_passengers}
                onChange={handleChange}
                placeholder={labels.numberOfPassengers}
                min="1"
                max="20"
                required
              />
            </div>
          </div>
          
          <div className="row mb-3">
            <div className="col-md-6 mb-3">
              <input
                type="text"
                className="form-control"
                name="destination_from"
                value={formData.destination_from}
                onChange={handleChange}
                placeholder={labels.destinationFrom}
                required
              />
            </div>
            <div className="col-md-6 mb-3">
              <input
                type="text"
                className="form-control"
                name="destination_to"
                value={formData.destination_to}
                onChange={handleChange}
                placeholder={labels.destinationTo}
                required
              />
            </div>
          </div>

          <div className="d-flex justify-content-between">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {labels.close}
            </button>
            <button type="submit" className="btn btn-primary">
              {labels.submit}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestQuoteForm;